import axios from 'axios';
// import { io, Socket } from 'socket.io-client';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://lavernaibackend.onrender.com/api';
const SOCKET_URL = process.env.REACT_APP_SOCKET_URL || 'https://lavernaibackend.onrender.com';

export interface Competitor {
  id: string;
  name: string;
  balance: number;
  wins: number;
  rating: number;
  status: string;
  isOnline: boolean;
  lastSeen?: string;
  sessionId?: string;
  avatar: string;
  accountType: 'student' | 'public';
  gradeLevel?: number;
  university?: string;
  totalMatches: number;
  email?: string;
  phoneNumber?: string;
  profilePicture?: string;
  createdAt?: string;
  // Add areas of interest for search functionality
  areasOfInterest?: string[];
  // Add educational and location information
  educationalInfo?: {
    isCurrentlyStudying: boolean;
    level: 'primary_school' | 'form_4_leaver' | 'tvet' | 'college' | 'university' | 'postgraduate' | 'not_studying';
    institution?: string;
    courseOfStudy?: string;
    yearOfStudy?: number;
    expectedGraduationYear?: number;
  };
  personalInfo?: {
    age: number;
    gender: 'male' | 'female' | 'other' | 'prefer_not_to_say';
    region: string;
    province: string;
    city?: string;
    country: string;
    timezone: string;
  };
}

export interface CompetitorsResponse {
  success: boolean;
  data: Competitor[];
  pagination?: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
  filters?: {
    status: string;
    accountType: string;
    minRating: number;
    maxRating: number;
    windowMinutes: number;
  };
}

export interface OnlineCountResponse {
  success: boolean;
  data: {
    onlineCount: number;
    lastUpdated: string;
    windowMinutes: number;
  };
}

export interface CompetitorsOverviewResponse {
  success: boolean;
  data: {
    onlineCount: number;
    offlineCount: number;
    totalCount: number;
    lastUpdated: string;
  };
}

export interface CompetitorSearchFilters {
  // Basic search
  searchQuery?: string; // General search term that searches across multiple fields
  
  // Specific field searches
  name?: string;
  age?: number;
  ageRange?: { min: number; max: number };
  region?: string;
  province?: string;
  city?: string;
  institution?: string;
  courseOfStudy?: string;
  yearOfStudy?: number;
  educationalLevel?: 'primary_school' | 'form_4_leaver' | 'tvet' | 'college' | 'university' | 'postgraduate' | 'not_studying';
  areasOfInterest?: string[]; // Array of interest terms to search for
  gender?: 'male' | 'female' | 'other' | 'prefer_not_to_say';
  
  // Existing filters
  status?: 'online' | 'offline' | 'all';
  accountType?: 'student' | 'public' | 'all';
  minRating?: number;
  maxRating?: number;
  page?: number;
  limit?: number;
  excludeCurrentUser?: boolean;
}

export interface CompetitorSearchResponse {
  success: boolean;
  data: Competitor[];
  pagination?: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
  searchInfo?: {
    query: string;
    matchedFields: string[];
    totalResults: number;
  };
}

class CompetitorService {
  private baseURL: string;
  // private socket: Socket | null = null;
  private statusListeners: Map<string, (status: string) => void> = new Map();

  constructor() {
    this.baseURL = API_BASE_URL;
  }

  // Initialize Socket.IO connection for real-time updates
  initializeSocket(token: string, userId: string) {
    // Temporarily disabled Socket.IO functionality
    console.log('Socket.IO functionality temporarily disabled');
    
    // if (this.socket) {
    //   this.socket.disconnect();
    // }

    // this.socket = io(SOCKET_URL, {
    //   auth: { token }
    // });

    // this.socket.on('connect', () => {
    //   console.log('Connected to competitor status socket');
    //   this.socket?.emit('authenticate', { userId, token });
    // });

    // this.socket.on('authenticated', (data: any) => {
    //   console.log('Authenticated with socket server:', data);
    // });

    // this.socket.on('userStatusChanged', (data: any) => {
    //   console.log('User status changed:', data);
    //   // Notify listeners about status changes
    //   this.statusListeners.forEach((listener) => {
    //     listener(data.status);
    //   });
    // });

    // this.socket.on('disconnect', () => {
    //   console.log('Disconnected from competitor status socket');
    // });

    // // Send heartbeat every 30 seconds to keep status active
    // setInterval(() => {
    //   if (this.socket?.connected) {
    //     this.socket.emit('heartbeat', { userId });
    //   }
    // }, 30000);
  }

  // Subscribe to status updates
  onStatusChange(listener: (status: string) => void) {
    const listenerId = Math.random().toString(36);
    this.statusListeners.set(listenerId, listener);
    return () => {
      this.statusListeners.delete(listenerId);
    };
  }

  // Disconnect socket
  disconnectSocket() {
    // Temporarily disabled Socket.IO functionality
    console.log('Socket.IO functionality temporarily disabled');
    
    // if (this.socket) {
    //   this.socket.disconnect();
    //   this.socket = null;
    // }
  }

  async getCompetitors(token: string, filters?: {
    status?: 'online' | 'offline' | 'all';
    accountType?: 'student' | 'public' | 'all';
    minRating?: number;
    maxRating?: number;
    page?: number;
    limit?: number;
    excludeCurrentUser?: boolean;
  }): Promise<CompetitorsResponse> {
    try {
      const params = new URLSearchParams();
      
      if (filters?.status) params.append('status', filters.status);
      if (filters?.accountType) params.append('accountType', filters.accountType);
      if (filters?.minRating) params.append('minRating', filters.minRating.toString());
      if (filters?.maxRating) params.append('maxRating', filters.maxRating.toString());
      if (filters?.page) params.append('page', filters.page.toString());
      if (filters?.limit) params.append('limit', filters.limit.toString());
      if (filters?.excludeCurrentUser) params.append('excludeCurrentUser', 'true');

      const response = await axios.get<CompetitorsResponse>(`${this.baseURL}/competitors/available?${params}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.data && response.data.success) {
        return response.data;
      }
      
      return {
        success: false,
        data: [],
        pagination: { page: 1, limit: 10, total: 0, pages: 0 }
      };
    } catch (error) {
      console.error('Error fetching competitors from production API:', error);
      
      // Fallback to local API route if production fails
      try {
        const localResponse = await axios.get<CompetitorsResponse>('/api/competitors', {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        
        return localResponse.data || {
          success: false,
          data: [],
          pagination: { page: 1, limit: 10, total: 0, pages: 0 }
        };
      } catch (localError) {
        console.error('Error fetching competitors from local API:', localError);
        throw new Error('Failed to fetch competitors from both production and local APIs');
      }
    }
  }

  async getAvailableCompetitors(token: string, page: number = 1, limit: number = 6, excludeCurrentUser: boolean = true): Promise<CompetitorsResponse> {
    return this.getCompetitors(token, { page, limit, excludeCurrentUser });
  }

  async getOnlineCompetitors(token: string, page: number = 1, limit: number = 6, excludeCurrentUser: boolean = true): Promise<CompetitorsResponse> {
    return this.getCompetitors(token, { status: 'online', page, limit, excludeCurrentUser });
  }

  async getOfflineCompetitors(token: string, page: number = 1, limit: number = 6, excludeCurrentUser: boolean = true): Promise<CompetitorsResponse> {
    return this.getCompetitors(token, { status: 'offline', page, limit, excludeCurrentUser });
  }

  async getOnlineCompetitorsCount(token: string): Promise<number> {
    try {
      // First try the main API endpoint
      const response = await axios.get<OnlineCountResponse>(`${this.baseURL}/competitors/online-count`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.data && response.data.success) {
        return response.data.data.onlineCount;
      }
      
      return 0;
    } catch (error) {
      console.error('Error fetching online competitors count:', error);
      
      // Fallback: get online competitors and count them
      try {
        const competitors = await this.getCompetitors(token, { 
          status: 'online', 
          limit: 1000,
          excludeCurrentUser: true 
        });
        return competitors.data.length;
      } catch (fallbackError) {
        console.error('Fallback method failed:', fallbackError);
        // Return a default value to prevent UI errors
        return 0;
      }
    }
  }

  async getCompetitorsOverview(token: string): Promise<{
    onlineCount: number;
    offlineCount: number;
    totalCount: number;
  }> {
    try {
      // Get total competitors count
      const allCompetitors = await this.getCompetitors(token, { limit: 1000 });
      const totalCount = allCompetitors.pagination?.total || allCompetitors.data.length;
      
      // Get online competitors count
      const onlineCount = await this.getOnlineCompetitorsCount(token);
      
      // Calculate offline count
      const offlineCount = totalCount - onlineCount;
      
      return {
        onlineCount,
        offlineCount: Math.max(0, offlineCount),
        totalCount
      };
    } catch (error) {
      console.error('Error getting competitors overview:', error);
      return {
        onlineCount: 0,
        offlineCount: 0,
        totalCount: 0
      };
    }
  }

  // Update user's online status (called when user logs in/out or becomes active)
  async updateUserStatus(token: string, userId: string, isActive: boolean, sessionId?: string): Promise<boolean> {
    try {
      const response = await axios.post(`${this.baseURL}/competitors/status`, {
        userId,
        isActive,
        sessionId
      }, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      return response.data && response.data.success;
    } catch (error) {
      console.error('Error updating user status:', error);
      return false;
    }
  }

  // Update status when user logs in
  async setUserOnline(token: string, userId: string): Promise<boolean> {
    const sessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    return this.updateUserStatus(token, userId, true, sessionId);
  }

  // Update status when user logs out
  async setUserOffline(token: string, userId: string): Promise<boolean> {
    return this.updateUserStatus(token, userId, false);
  }

  // Update status via socket for real-time updates
  updateStatusViaSocket(status: 'online' | 'offline', sessionId?: string) {
    // Temporarily disabled Socket.IO functionality
    console.log('Socket.IO functionality temporarily disabled');
    
    // if (this.socket?.connected) {
    //   this.socket.emit('updateStatus', { status, sessionId });
    // }
  }

  // Start periodic status refresh to simulate real-time updates
  startStatusRefresh(token: string, userId: string, callback: (silent?: boolean) => void) {
    // Update user status to online when starting refresh
    this.setUserOnline(token, userId);
    
    // Refresh status every 30 seconds
    const interval = setInterval(async () => {
      try {
        // Keep user status active
        await this.updateUserStatus(token, userId, true);
        // Call the callback with silent=true to refresh UI without loading state
        callback(true);
      } catch (error) {
        console.error('Error refreshing status:', error);
      }
    }, 30000);

    // Return function to stop the refresh
    return () => {
      clearInterval(interval);
      this.setUserOffline(token, userId);
    };
  }

  // New comprehensive search method
  async searchCompetitors(token: string, filters: CompetitorSearchFilters): Promise<CompetitorSearchResponse> {
    try {
      const params = new URLSearchParams();
      
      // Add search query
      if (filters.searchQuery) params.append('searchQuery', filters.searchQuery);
      
      // Add specific field searches
      if (filters.name) params.append('name', filters.name);
      if (filters.age) params.append('age', filters.age.toString());
      if (filters.ageRange) {
        params.append('minAge', filters.ageRange.min.toString());
        params.append('maxAge', filters.ageRange.max.toString());
      }
      if (filters.region) params.append('region', filters.region);
      if (filters.province) params.append('province', filters.province);
      if (filters.city) params.append('city', filters.city);
      if (filters.institution) params.append('institution', filters.institution);
      if (filters.courseOfStudy) params.append('courseOfStudy', filters.courseOfStudy);
      if (filters.yearOfStudy) params.append('yearOfStudy', filters.yearOfStudy.toString());
      if (filters.educationalLevel) params.append('educationalLevel', filters.educationalLevel);
      if (filters.areasOfInterest && filters.areasOfInterest.length > 0) {
        params.append('areasOfInterest', filters.areasOfInterest.join(','));
      }
      if (filters.gender) params.append('gender', filters.gender);
      
      // Add existing filters
      if (filters.status) params.append('status', filters.status);
      if (filters.accountType) params.append('accountType', filters.accountType);
      if (filters.minRating) params.append('minRating', filters.minRating.toString());
      if (filters.maxRating) params.append('maxRating', filters.maxRating.toString());
      if (filters.page) params.append('page', filters.page.toString());
      if (filters.limit) params.append('limit', filters.limit.toString());
      if (filters.excludeCurrentUser) params.append('excludeCurrentUser', 'true');

      const response = await axios.get<CompetitorSearchResponse>(`${this.baseURL}/competitors/search?${params}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.data && response.data.success) {
        return response.data;
      }
      
      return {
        success: false,
        data: [],
        pagination: { page: 1, limit: 10, total: 0, pages: 0 }
      };
    } catch (error) {
      console.error('Error searching competitors:', error);
      
      // Fallback: perform client-side search on existing competitors
      try {
        const allCompetitors = await this.getCompetitors(token, { 
          limit: 1000, 
          excludeCurrentUser: filters.excludeCurrentUser 
        });
        
        const filteredCompetitors = this.performClientSideSearch(allCompetitors.data, filters);
        
        return {
          success: true,
          data: filteredCompetitors,
          pagination: {
            page: filters.page || 1,
            limit: filters.limit || 10,
            total: filteredCompetitors.length,
            pages: Math.ceil(filteredCompetitors.length / (filters.limit || 10))
          },
          searchInfo: {
            query: filters.searchQuery || 'Client-side search',
            matchedFields: this.getMatchedFields(filters),
            totalResults: filteredCompetitors.length
          }
        };
      } catch (fallbackError) {
        console.error('Client-side search fallback failed:', fallbackError);
        throw new Error('Failed to search competitors');
      }
    }
  }

  // Client-side search fallback method
  private performClientSideSearch(competitors: Competitor[], filters: CompetitorSearchFilters): Competitor[] {
    return competitors.filter(competitor => {
      // Search query - searches across multiple fields
      if (filters.searchQuery) {
        const searchTerm = filters.searchQuery.toLowerCase();
        const searchableFields = [
          competitor.name,
          competitor.personalInfo?.age?.toString(),
          competitor.personalInfo?.region,
          competitor.personalInfo?.province,
          competitor.personalInfo?.city,
          competitor.educationalInfo?.institution,
          competitor.educationalInfo?.courseOfStudy,
          competitor.educationalInfo?.yearOfStudy?.toString(),
          competitor.educationalInfo?.level,
          // Add areas of interest if available
          ...(competitor.areasOfInterest || [])
        ].filter(Boolean).map(field => field?.toLowerCase());
        
        const hasMatch = searchableFields.some(field => 
          field && field.includes(searchTerm)
        );
        
        if (!hasMatch) return false;
      }
      
      // Specific field searches
      if (filters.name && !competitor.name.toLowerCase().includes(filters.name.toLowerCase())) {
        return false;
      }
      
      if (filters.age && competitor.personalInfo?.age !== filters.age) {
        return false;
      }
      
      if (filters.ageRange) {
        const age = competitor.personalInfo?.age;
        if (!age || age < filters.ageRange.min || age > filters.ageRange.max) {
          return false;
        }
      }
      
      if (filters.region && competitor.personalInfo?.region?.toLowerCase() !== filters.region.toLowerCase()) {
        return false;
      }
      
      if (filters.province && competitor.personalInfo?.province?.toLowerCase() !== filters.province.toLowerCase()) {
        return false;
      }
      
      if (filters.city && competitor.personalInfo?.city?.toLowerCase() !== filters.city.toLowerCase()) {
        return false;
      }
      
      if (filters.institution && competitor.educationalInfo?.institution?.toLowerCase() !== filters.institution.toLowerCase()) {
        return false;
      }
      
      if (filters.courseOfStudy && competitor.educationalInfo?.courseOfStudy?.toLowerCase() !== filters.courseOfStudy.toLowerCase()) {
        return false;
      }
      
      if (filters.yearOfStudy && competitor.educationalInfo?.yearOfStudy !== filters.yearOfStudy) {
        return false;
      }
      
      if (filters.educationalLevel && competitor.educationalInfo?.level !== filters.educationalLevel) {
        return false;
      }
      
      if (filters.areasOfInterest && filters.areasOfInterest.length > 0) {
        const competitorInterests = competitor.areasOfInterest || [];
        const hasMatchingInterest = filters.areasOfInterest.some(interest =>
          competitorInterests.some(compInterest => 
            compInterest.toLowerCase().includes(interest.toLowerCase())
          )
        );
        if (!hasMatchingInterest) return false;
      }
      
      if (filters.gender && competitor.personalInfo?.gender !== filters.gender) {
        return false;
      }
      
      // Existing filters
      if (filters.status && filters.status !== 'all') {
        const isOnline = competitor.isOnline;
        if (filters.status === 'online' && !isOnline) return false;
        if (filters.status === 'offline' && isOnline) return false;
      }
      
      if (filters.accountType && filters.accountType !== 'all') {
        if (filters.accountType !== competitor.accountType) return false;
      }
      
      if (filters.minRating && competitor.rating < filters.minRating) {
        return false;
      }
      
      if (filters.maxRating && competitor.rating > filters.maxRating) {
        return false;
      }
      
      return true;
    });
  }

  // Helper method to get matched fields for search info
  private getMatchedFields(filters: CompetitorSearchFilters): string[] {
    const matchedFields: string[] = [];
    
    if (filters.searchQuery) matchedFields.push('general');
    if (filters.name) matchedFields.push('name');
    if (filters.age || filters.ageRange) matchedFields.push('age');
    if (filters.region) matchedFields.push('region');
    if (filters.province) matchedFields.push('province');
    if (filters.city) matchedFields.push('city');
    if (filters.institution) matchedFields.push('institution');
    if (filters.courseOfStudy) matchedFields.push('courseOfStudy');
    if (filters.yearOfStudy) matchedFields.push('yearOfStudy');
    if (filters.educationalLevel) matchedFields.push('educationalLevel');
    if (filters.areasOfInterest) matchedFields.push('areasOfInterest');
    if (filters.gender) matchedFields.push('gender');
    if (filters.status) matchedFields.push('status');
    if (filters.accountType) matchedFields.push('accountType');
    if (filters.minRating || filters.maxRating) matchedFields.push('rating');
    
    return matchedFields;
  }
}

export default new CompetitorService();
